import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

export enum Menu {
  LEFT = "left",
  RIGHT = "right",
}

export const SideMenuContext = createContext({
  isOpen: false,
  closable: true,
  currentMenu: undefined,
  openLeftMenu(_?: boolean) {},
  openRightMenu(_?: boolean) {},
  closeMenu() {},
});
export const useSideMenuContext = () => useContext(SideMenuContext);

const defaultMenu = { menu: undefined, closable: true } as const;

export const SideMenuProvider = ({ children }: WithChildren) => {
  const [{ menu: currentMenu, closable: currentlyClosable }, setCurrentMenu] =
    useState<{
      menu: Menu;
      closable: boolean;
    }>(defaultMenu);

  const location = useLocation();
  const previousPathnameRef = useRef<string>(location.pathname);
  const closeTimeoutRef = useRef<number | null>(null);

  const isOpen = currentMenu !== undefined;

  const openLeftMenu = useCallback((closable = true) => {
    // Clear any pending close timeout to prevent race conditions
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setCurrentMenu({ menu: Menu.LEFT, closable });
  }, []);
  const openRightMenu = useCallback((closable = true) => {
    // Clear any pending close timeout to prevent race conditions
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setCurrentMenu({ menu: Menu.RIGHT, closable });
  }, []);
  const closeMenu = useCallback(() => {
    // Clear any existing timeout to prevent race conditions
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    // Add small delay to prevent Material-UI Drawer timing issues
    closeTimeoutRef.current = window.setTimeout(() => {
      setCurrentMenu(defaultMenu);
      closeTimeoutRef.current = null;
    }, 100);
  }, []);

  // Auto-close menu when navigating to a different route
  useEffect(() => {
    const currentPathname = location.pathname;
    const previousPathname = previousPathnameRef.current;

    // Only close menu if pathname actually changed and menu is open
    if (isOpen && currentPathname !== previousPathname) {
      closeMenu();
    }

    // Update the ref for next time
    previousPathnameRef.current = currentPathname;
  }, [location.pathname, isOpen, closeMenu]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      currentMenu,
      closable: currentlyClosable,
      setCurrentMenu,
      openLeftMenu,
      openRightMenu,
      closeMenu,
    }),
    [
      isOpen,
      currentMenu,
      currentlyClosable,
      openLeftMenu,
      openRightMenu,
      closeMenu,
    ],
  );

  return (
    <SideMenuContext.Provider value={value}>
      {children}
    </SideMenuContext.Provider>
  );
};
